import { Component, effect, inject, OnInit, signal, untracked, viewChild } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonText,
  IonTitle,
  IonToolbar,
  NavController,
} from '@ionic/angular/standalone';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { withUpdateAddress } from '@libs/mtjr/shared/generated/entities/user/user-store-features';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { TranslatePipe } from '@ngx-translate/core';
import { LocationCoordinates, LocationMapComponent } from './location-map.component';

import { BASE_CONFIG } from '@libs/core/base-config';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { errorState } from '@libs/core/lib/state/async-states';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { addressUpdateUtils } from '@libs/mtjr/shared/generated/forms';
import { patchState, signalStore, withMethods } from '@ngrx/signals';

export const AddressEditStore = signalStore(
  withUpdateAddress(),
  withMethods((store) => ({
    setFailure(failure: Failure) {
      patchState(store, { updateAddressState: errorState(failure) });
    },
  })),
);

@Component({
  selector: 'mtjr-edit-address',
  providers: [AddressEditStore],
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButtons,
    VxTextFieldComponent,
    ReactiveFormsModule,
    FormsModule,
    VxSubmitButtonComponent,
    TranslatePipe,
    VxErrorBarComponent,
    IonText,
    IonBackButton,
    LocationMapComponent,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-title>{{ 'COMMON.EDIT' | translate }}</ion-title>
        <ion-buttons slot="start">
          <ion-back-button defaultHref="/account/addresses" [text]="'COMMON.BACK' | translate" />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      @if (addressToEdit()) {
        <div class="flex">
          <div class="mx-auto w-full max-w-[500px] pt-4">
            <form [formGroup]="updateUtils.form" (ngSubmit)="save()">
              <div class="mt-2 px-5 py-3">
                <fieldset>
                  <mtjr-text-field
                    [field]="updateUtils.form.controls.label"
                    [label]="'ADDRESS.NAME' | translate"
                    [placeholder]="'ADDRESS.NAME_EXAMPLE' | translate"
                  />

                  <div class="my-3"></div>
                  <div class="my-3"></div>
                  <mtjr-text-field [field]="updateUtils.form.controls.street" [label]="'ADDRESS.ADDRESS' | translate" />
                  <div class="my-3"></div>
                  <mtjr-text-field [field]="updateUtils.form.controls.phone" [label]="'ADDRESS.PHONE' | translate" />
                  <div class="my-3"></div>
                  <mtjr-text-field [field]="updateUtils.form.controls.phone2" [label]="'ADDRESS.PHONE2' | translate" />

                  <div class="mt-3 mb-2 py-2 flex items-center justify-between">
                    <ion-text>{{ 'ADDRESS.LOCATION' | translate }}</ion-text>
                    @if (updateUtils.form.controls.longitude.value && updateUtils.form.controls.latitude.value) {
                      <ion-text color="success">
                        {{ 'ADDRESS.LOCATION_SELECTED' | translate }}
                      </ion-text>
                    }
                  </div>

                  <!-- Map Component -->
                  @defer {
                    <mtjr-location-map
                      [coordinates]="getCoordinates()"
                      (locationSelected)="onLocationSelected($event)"
                    />
                  } @loading {
                    <div
                      class="mt-3 mb-2 py-8 flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-blue-300"
                    >
                      <div class="text-blue-500 mb-2">
                        <svg
                          class="w-12 h-12 mx-auto mb-2 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                          ></circle>
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  } @placeholder {
                    <div
                      class="mt-3 mb-2 py-8 flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-blue-300"
                    >
                      <div class="text-blue-500 mb-2">
                        <svg
                          class="w-12 h-12 mx-auto mb-2 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                          ></circle>
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  }
                </fieldset>
              </div>
              <div class="mb-4 mx-5">
                <mtjr-error-bar [failure]="editStore.updateAddressFailure()" />
              </div>

              <div class="mx-4">
                <mtjr-submit-button
                  data-cy="address-submit-button"
                  [label]="'COMMON.SAVE' | translate"
                  [state]="editStore.updateAddressState()"
                />
              </div>
            </form>
          </div>
        </div>
      } @else {
        <h1>Error</h1>
        <h2>{{ route.snapshot.queryParams['addressId'] }}</h2>
      }
    </ion-content>
  `,
})
export class EditAddressPage implements OnInit {
  mapComponent = viewChild.required(LocationMapComponent);

  editStore = inject(AddressEditStore);
  userStore = inject(UserStore);
  route = inject(ActivatedRoute);
  addressToEdit = signal<Entity.UserAddress | undefined>(undefined);
  updateUtils = addressUpdateUtils(inject(NonNullableFormBuilder));
  router = inject(Router);
  alerts = inject(AppAlerts);
  navController = inject(NavController);
  isVendor = inject(BASE_CONFIG).flavor === 'vendors';

  back() {
    const url = this.isVendor ? '/main/account/addresses' : '/account/addresses';
    this.router.navigate([url], { queryParams: this.route.snapshot.queryParams });
  }

  private populateAddress() {
    const addressId = this.route.snapshot.paramMap.get('addressId');
    if (!addressId) return;
    const address = this.userStore.addresses().find((a) => a.id === addressId);
    if (address) {
      this.addressToEdit.set(address);
      this.updateUtils.form.patchValue({
        label: address.label,
        street: address.street,
        internalAddress: address.internalAddress,
        phone: address.phone,
        phone2: address.phone2,
        latitude: address.latitude,
        longitude: address.longitude,
      });
    }
  }

  constructor() {
    effect(() => {
      if (!this.userStore.loggedInUserState().success) return;
      untracked(() => {
        this.populateAddress();
      });
    });
    effect(async () => {
      if (this.editStore.updateAddressState().success) {
        this.back();
        untracked(() => {
          this.userStore.loggedInUser();
        });
      }
    });
  }
  ngOnInit(): void {
    this.populateAddress();
    if (!this.addressToEdit()) {
      this.userStore.loggedInUser();
    }
  }

  choseCurrentLocation() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.alerts.successSnackbarTR('ADDRESS.GEOLOCATION_SUCCESS');
          this.updateUtils.form.patchValue({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          this.updateUtils.form.controls.latitude.markAsDirty();
          this.updateUtils.form.controls.longitude.markAsDirty();

          // Perform reverse geocoding to get address text
          this.reverseGeocode(position.coords.latitude, position.coords.longitude)
            .then((address) => {
              if (address) {
                this.updateUtils.form.patchValue({
                  internalAddress: address,
                });
              }
            })
            .catch((error) => {
              console.warn('Failed to get address from coordinates:', error);
            });
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              this.alerts.dangerSnackbarTR('ADDRESS.GEOLOCATION_PERMISSION_DENIED');
              break;
            case error.POSITION_UNAVAILABLE:
              this.alerts.dangerSnackbarTR('ADDRESS.GEOLOCATION_POSITION_UNAVAILABLE');
              break;
            case error.TIMEOUT:
              this.alerts.dangerSnackbarTR('ADDRESS.GEOLOCATION_POSITION_UNAVAILABLE');
              break;
            default:
              console.error('An unknown error occurred.');
              break;
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      console.warn('Geolocation is not supported by this browser.');
      this.alerts.dangerSnackbarTR('ADDRESS.GEOLOCATION_POSITION_UNAVAILABLE');
    }
  }

  getCoordinates(): LocationCoordinates | null {
    const lat = this.updateUtils.form.controls.latitude.value;
    const lng = this.updateUtils.form.controls.longitude.value;

    if (lat && lng) {
      return { latitude: lat, longitude: lng };
    }
    return null;
  }

  async onLocationSelected(coordinates: LocationCoordinates) {
    this.updateUtils.form.patchValue({
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    });

    // Mark as dirty to indicate changes
    this.updateUtils.form.controls.latitude.markAsDirty();
    this.updateUtils.form.controls.longitude.markAsDirty();

    // Perform reverse geocoding to get address text
    try {
      const address = await this.reverseGeocode(coordinates.latitude, coordinates.longitude);
      if (address) {
        this.updateUtils.form.patchValue({
          internalAddress: address,
        });
        this.updateUtils.form.controls.internalAddress.markAsDirty();
      }
    } catch (error) {
      console.warn('Failed to get address from coordinates:', error);
    }

    this.alerts.successSnackbarTR('ADDRESS.LOCATION_SELECTED');
  }

  private async reverseGeocode(lat: number, lng: number): Promise<string | null> {
    console.log('reverseGeocode...');

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=ar`,
      );

      if (!response.ok) {
        throw new Error('Geocoding request failed');
      }

      const data = await response.json();

      console.log(data.address);

      // Extract meaningful address components
      const address = data.address;
      if (!address) return null;

      // Build address string from components
      const addressParts = new Set<string>();

      // Add city or town
      // if (address.city) addressParts.add(address.city);
      // if (address.state) addressParts.add(address.state);
      if (address.town) addressParts.add(address.town);
      if (address.city_district) addressParts.add(address.city_district);
      if (address.village) addressParts.add(address.village);
      if (address.suburb) addressParts.add(address.suburb);
      if (address.road) addressParts.add(address.road);

      return Array.from(addressParts).join(', ') || data.display_name || null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  }

  save() {
    const addressId = this.addressToEdit()?.id;
    if (!addressId) {
      return this.editStore.setFailure(new LocalFailure());
    }

    if (this.updateUtils.form.invalid) {
      return this.updateUtils.form.markAllAsTouched();
    }
    const result = this.updateUtils.parse();
    if (!result.success) {
      return this.editStore.setFailure(new LocalFailure());
    }
    if (Object.keys(result.data).length === 0) {
      this.back();
      this.alerts.snackbarTR('COMMON.NO_CHANGES_MADE');
      return;
    }
    this.editStore.updateAddress(result.data, { addressId });
  }
}
